import { PIIMaskRules, defaultPIIMaskRules } from '@ringcx/pii-interceptor';

import { BaseTransport, LogParams } from './transport/base';
import { ConsoleTransport } from './transport/console';
import { LOG_LEVELS, LogLevel } from './transport/constants';
import { IndexedDBTransport } from './transport/indexedDB';

export enum Transport {
    CONSOLE = 'console',
    INDEXED_DB = 'indexeddb',
}
export interface LogConfig {
    level?: LogLevel;
    transports?: Transport[];
}
export class RCXLogger {
    private static _instance: RCXLogger;
    private _config: LogConfig;
    private _transports!: BaseTransport[];
    private _allowedLevels!: Set<LogLevel>;
    private _tempPiiMaskRules?: PIIMaskRules;
    constructor() {
        this._config = {
            level: LogLevel.INFO,
            transports: [Transport.CONSOLE, Transport.INDEXED_DB],
        };
        this._initFromConfig();
    }
    private _initFromConfig() {
        this._setLogLevel();
        this._initTransports();
    }
    private _setLogLevel() {
        if (this._config.level) {
            let baseLevelIndex = LOG_LEVELS.indexOf(this._config.level);
            if (baseLevelIndex === -1) {
                baseLevelIndex = 3;
            } else {
                baseLevelIndex += 1;
            }
            this._allowedLevels = new Set(LOG_LEVELS.slice(0, baseLevelIndex));
        }
    }
    private _initTransports() {
        if (Array.isArray(this._config.transports)) {
            this._transports = this._config.transports.map((transport) => this._createTransport(transport));
        }
    }
    private _createTransport(transport: Transport): BaseTransport {
        switch (transport) {
            case 'console':
                return new ConsoleTransport();
            case 'indexeddb':
                return new IndexedDBTransport();
            default:
                return new ConsoleTransport();
        }
    }
    static getInstance() {
        if (!RCXLogger._instance) {
            RCXLogger._instance = new RCXLogger();
            //@ts-ignore
            window[Symbol.for('rcx_logger')] = RCXLogger._instance;
        }
        return RCXLogger._instance;
    }
    /**
     * @param config.level Level of logs. Only logs with level less than or equal to this will be logged.
     */
    public setLogConfig(config: LogConfig) {
        if (Array.isArray(config.transports)) {
            config.transports = Array.from(new Set(config.transports));
        }
        this._config = Object.assign(this._config, config);
        this._initFromConfig();
    }

    public setUserConfig(agentSettings: any) {
        (this._transports[1] as IndexedDBTransport).initUserConfig(agentSettings);
    }

    public log(params: LogParams) {
        if (!this._allowedLevels.has(params.level)) {
            return;
        }
        const timestamp = new Date().toISOString();
        params.messageParts?.unshift(`[${timestamp}]`);
        this._transports.forEach((transport) => {
            transport.log(params);
        });
        this._tempPiiMaskRules = undefined;
    }
    public error(...messageParts: any[]) {
        this.log({ level: LogLevel.ERROR, messageParts, piiMaskRules: this._tempPiiMaskRules });
    }
    public warn(...messageParts: any[]) {
        this.log({ level: LogLevel.WARN, messageParts, piiMaskRules: this._tempPiiMaskRules });
    }
    public info(...messageParts: any[]) {
        this.log({ level: LogLevel.INFO, messageParts, piiMaskRules: this._tempPiiMaskRules });
    }
    public debug(...messageParts: any[]) {
        this.log({ level: LogLevel.DEBUG, messageParts, piiMaskRules: this._tempPiiMaskRules });
    }

    public event(...messageParts: any[]) {
        this.log({ level: LogLevel.EVENT, messageParts, piiMaskRules: this._tempPiiMaskRules });
    }
    public verbose(...messageParts: any[]) {
        this.log({ level: LogLevel.VERBOSE, messageParts, piiMaskRules: this._tempPiiMaskRules });
    }

    public setPiiMaskRulesOnce(piiMaskRules: PIIMaskRules, mergeWithDefault?: boolean) {
        this._tempPiiMaskRules = mergeWithDefault ? { ...defaultPIIMaskRules, ...piiMaskRules } : piiMaskRules;
        return this;
    }

    getLogRecordsByTime(time: number) {
        const all = this._transports
            .filter((transport) => transport instanceof IndexedDBTransport)
            .map((transport) => (transport as IndexedDBTransport).getLogRecordsByTime(time));
        return Promise.all(all).then((results) => results.flat());
    }
    getAllLogRecords() {
        const all = this._transports
            .filter((transport) => transport instanceof IndexedDBTransport)
            .map((transport) => (transport as IndexedDBTransport).getAllLogRecords());
        return Promise.all(all).then((results) => results.flat());
    }

    downloadLogs() {
        this._transports.forEach((transport) => {
            transport.downloadLogs();
        });
    }

    downloadLogsByRange(startDate?: Date, endDate?: Date, limit?: number) {
        (this._transports[1] as IndexedDBTransport).downloadLogsByRange(startDate, endDate, limit);
    }
}
